import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

export const APP_NAME = "SourceMagnet";

export type SectionTitle = (t: TFunction) => string;

export const documentTitle = (...parts: Array<string | null | undefined>): string =>
  [...parts.map((part) => part?.trim()).filter((part): part is string => !!part), APP_NAME].join(" – ");

type PageTitleContextValue = {
  setRecord: (record: string | null) => void;
  setSection: (section: string | null) => void;
};

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export const PageTitle = ({ section, children }: { section: SectionTitle; children: ReactNode }) => {
  const { t } = useTranslation();
  const [record, setRecord] = useState<string | null>(null);
  const [sectionOverride, setSection] = useState<string | null>(null);
  const routeSection = section(t);
  const shownSection = sectionOverride ?? routeSection;

  useEffect(() => {
    document.title = documentTitle(record, shownSection);
  }, [record, shownSection]);

  const value = useMemo(() => ({ setRecord, setSection }), []);
  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
};

export const useRecordTitle = (record: string | null | undefined, section?: string | null) => {
  const context = useContext(PageTitleContext);
  useEffect(() => {
    if (!context) return;
    context.setRecord(record?.trim() ? record : null);
    context.setSection(section?.trim() ? section : null);
  }, [context, record, section]);
  useEffect(
    () => () => {
      context?.setRecord(null);
      context?.setSection(null);
    },
    [context],
  );
};
